import { TooltipProps, SelectProps, Tooltip, MenuProps } from "@mui/material";
import { CoreSelect, CoreMenuItem } from "@verity/components/core";
import { useState, useId, useRef, useEffect } from "react";
import { useInstructionEditorTooltipContext } from "./InstructionEditorContext";
import {
	FormatValueMap,
	Format,
	fontSizes,
	renderSizeOption,
	headingOptions,
	renderHeaderOption,
	textColors,
	renderColorSquare,
	colorMenuProps,
	renderColorValue,
	renderBackgroundValue,
	fontFamilies,
	renderFontOption,
	alignOptions,
	renderAlignOption,
	renderAlignValue,
} from "./InstructionEditorTypes";
import i18n from "../i18n";

interface TooltipWrapperProps {
	/** Unique id to match against context for showing tooltip */
	id: string;
	title: string;
	/**For selects only */
	selectOpen?: boolean;
	children: React.ReactNode;
	/** Enables tooltip visibility for select options.
	 * Since focus lands on the MenuItem (not the child), we pass this from the MenuItem
	 * to manually trigger the tooltip on keyboard focus.
	 */
	optionForceOpen?: boolean;
	placement?: TooltipProps["placement"];
	offset?: number;
}

/**
 * Tooltip wrapper wraps around QuillSelect and QuillButton.
 * Created this to manage tooltip states and have one location where context is used
 */
function TooltipWrapper({
	id,
	title,
	selectOpen = false,
	children,
	optionForceOpen = false,
	placement = "bottom",
	offset = 0,
}: TooltipWrapperProps) {
	const { activeId, setActiveId } = useInstructionEditorTooltipContext();
	const open = (optionForceOpen || activeId === id) && !selectOpen;

	return (
		<Tooltip
			title={title}
			open={open}
			placement={placement}
			slotProps={{
				popper: {
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, offset],
							},
						},
					],
				},
			}}
		>
			<span
				style={{ display: "inline-block" }}
				onMouseEnter={() => setActiveId(id)}
				onMouseLeave={() => setActiveId(null)}
				onFocus={() => setActiveId(id)}
				onBlur={() => setActiveId(null)}
				aria-label={undefined} // Remove MUI Tooltip from injecting aria-label -> aria-labels are on children
			>
				{children}
			</span>
		</Tooltip>
	);
}

interface QuillSelectProps<K extends keyof FormatValueMap> {
	format: K;
	formats: Partial<FormatValueMap>;
	onFormatChange: <T extends K>(f: T, v: FormatValueMap[T]) => void;
	options: { label: string; value: FormatValueMap[K] }[];
	ariaLabel: string;
	parser?: (raw: string) => FormatValueMap[K];

	/**
	 * Optional: if you want to render each option (label/value) with custom styling,
	 * provide a function that returns a ReactNode.
	 */
	renderOption?: (value: FormatValueMap[K], label: string) => React.ReactNode;
	/**
	 * Optional MUI MenuProps override, e.g. to force a grid layout for color swatches.
	 */
	menuProps?: Partial<MenuProps>;
	renderValue?: SelectProps<string>["renderValue"];
	hideIcon?: boolean;
	/**Pass true if each dropdown items need tooltip. Tooltip text will be label from renderOption */
	optionHasTooltip?: boolean;
}

function QuillSelect<K extends keyof FormatValueMap>(
	props: QuillSelectProps<K> & Omit<SelectProps<string>, "onChange" | "value">,
) {
	const {
		/**
		 * Which Quill format key this component coresponds to
		 */
		format,
		/**
		 * Format states
		 */
		formats,
		onFormatChange,
		/**
		 * The set of options this dropdown should render. Each “value” must match
		 * FormatValueMap[K].
		 */
		options,
		/**
		 * Aria label for dropdown
		 */
		ariaLabel,
		/**
		 * If your “options” values come in as strings but Quill expects a different type
		 * (like for "header": Quill wants a number or ""), you can pass a parser here.
		 * The parser takes raw string => typed FormatValueMap[K].
		 */
		parser,
		/**
		 * Optional: if you want to render each option (label/value) with custom styling,
		 * provide a function that returns a ReactNode.
		 * Example usage:
		 *   renderOption(value, label) => <Typography variant={`h${value}`}>{label}</Typography>
		 */
		renderOption,
		hideIcon = false,
		optionHasTooltip = false,
	} = props;

	const currentValue = formats[format] ?? "";

	const [selectOpen, setSelectOpen] = useState(false);
	const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);

	// Select tooltip Id
	const id = useId();
	const idOptionSuffix = useId(); // Id suffix for options, for if options need a tooltip

	return (
		<TooltipWrapper title={ariaLabel} id={id} selectOpen={selectOpen}>
			<CoreSelect
				onOpen={() => setSelectOpen(true)}
				onClose={() => setSelectOpen(false)}
				label={ariaLabel}
				size="small"
				value={String(currentValue)}
				MenuProps={props.menuProps}
				onChange={(e) => {
					const raw = e.target.value;
					const typedValue = parser ? parser(raw) : (raw as unknown as FormatValueMap[K]);
					onFormatChange(format, typedValue);
				}}
				displayEmpty
				sx={{
					fontSize: "14px",
					fontWeight: 500,
					padding: "0 2px",
					margin: "1px",
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "transparent",
					},
					"& .MuiOutlinedInput-notchedOutline": {
						border: "none",
					},
					"& .MuiSelect-select": {
						padding: hideIcon ? "1px !important" : undefined,
					},
				}}
				renderValue={
					props.renderValue ??
					((value: string) => {
						const found = options.find((opt) => String(opt.value) === value);
						return found ? found.label : "";
					})
				}
				IconComponent={hideIcon ? () => null : undefined}
			>
				{options.map(({ value, label }) => {
					const optionId = `${label}-${idOptionSuffix}`;
					const key = String(value) || "normal";
					const isFocused = focusedOptionId === optionId;

					return (
						<CoreMenuItem
							key={key}
							value={key}
							onFocus={() => setFocusedOptionId(optionId)}
							onBlur={() => setFocusedOptionId(null)}
							onMouseEnter={() => setFocusedOptionId(optionId)}
							onMouseLeave={() => setFocusedOptionId(null)}
							aria-label={optionHasTooltip ? label : undefined}
						>
							{optionHasTooltip ? (
								<TooltipWrapper id={optionId} title={label} optionForceOpen={isFocused} placement={"right"}>
									<>{renderOption ? renderOption(value, label) : label}</>
								</TooltipWrapper>
							) : renderOption ? (
								renderOption(value, label)
							) : (
								label
							)}
						</CoreMenuItem>
					);
				})}
			</CoreSelect>
		</TooltipWrapper>
	);
}

function QuillButton({
	className,
	ariaLabel,
	value,
	isToggleButton = false,
}: {
	className: string;
	ariaLabel: string;
	value?: string;
	isToggleButton?: boolean;
}) {
	const id = useId();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [isPressed, setIsPressed] = useState(false);

	// I tried updating aria-pressed using react-quill's format state. But it wasn't in sync with the toolbar.
	// Example, if you're on a blank line, and click for example, "Bold", even though the toolbar shows the Bold button as active, the format state showed "false"
	// Thus why I just listen for class changes now.
	useEffect(() => {
		if (!isToggleButton) return;
		const el = buttonRef.current;
		if (!el) return;

		// Initialize isPressed from the button’s own class list
		setIsPressed(el.classList.contains("ql-active"));

		// Create a MutationObserver to watch for class changes on the button
		const observer = new MutationObserver(() => {
			setIsPressed(el.classList.contains("ql-active"));
		});

		observer.observe(el, { attributes: true, attributeFilter: ["class"] });

		return () => observer.disconnect();
	}, [isToggleButton]);

	// react quill wants their buttons as <button>
	return (
		<TooltipWrapper title={ariaLabel} id={id} offset={6.5}>
			<button
				ref={isToggleButton ? buttonRef : undefined}
				type="button"
				className={`${className} quill-button`}
				value={value}
				aria-pressed={isToggleButton ? isPressed : undefined}
				aria-label={ariaLabel}
			/>
		</TooltipWrapper>
	);
}

function QuillGroup({ ariaLabel, children }: { ariaLabel?: string; children: React.ReactNode }) {
	return (
		<span className="ql-formats" role={ariaLabel ? "group" : undefined} aria-label={ariaLabel}>
			{children}
		</span>
	);
}

type CustomToolBarProps = {
	onFormatChange: <K extends Format>(format: K, value: FormatValueMap[K]) => void;
	formats: Partial<FormatValueMap>;
	toolbarId: string;
};

export const CustomToolBar = ({ onFormatChange, formats, toolbarId }: CustomToolBarProps) => (
	<div id={toolbarId} role="toolbar" aria-label={i18n.t("instructionEditorTSX.ariaLabels.toolbar")}>
		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.textFormattingOptions")}>
			<QuillButton
				className="ql-bold"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.bold")}
				isToggleButton
			/>
			<QuillButton
				className="ql-italic"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.italic")}
				isToggleButton
			/>
			<QuillButton
				className="ql-underline"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.underline")}
				isToggleButton
			/>
			<QuillButton
				className="ql-strike"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.strikethrough")}
				isToggleButton
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.blockFormattingOptions")}>
			<QuillButton
				className="ql-blockquote"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.blockquote")}
				isToggleButton
			/>
			<QuillButton
				className="ql-code-block"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.codeBlock")}
				isToggleButton
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.insertOptions")}>
			{/**
			 * There is a compatibility issue with the "insert link button".
			 * React-Quill v2 uses Quill v2 internally.
			 *
			 * In Quill v2, the built-in "tooltip" module (which the insert link button relies on) was removed.
			 * But react-quill’s link button still tries to use this tooltip module.
			 *
			 * TDLR: the insert link button doesn’t work correctly because react-quill tries to find tooltip module which doesn't exist.
			 */}

			{/* <QuillButton
                className="ql-link"
                ariaLabel={i18nPackage.t("instructionEditorTSX.ariaLabels.button.insertLink")}
                isActive={!!formats.link}
            /> */}
			<QuillButton className="ql-image" ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.insertImage")} />
			<QuillButton className="ql-video" ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.insertVideo")} />
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.headingButtons")}>
			<QuillButton
				className="ql-header"
				value="1"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.heading1")}
				isToggleButton
			/>
			<QuillButton
				className="ql-header"
				value="2"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.heading2")}
				isToggleButton
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.listOptions")}>
			<QuillButton
				className="ql-list"
				value="ordered"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.orderedList")}
				isToggleButton
			/>
			<QuillButton
				className="ql-list"
				value="bullet"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.bulletList")}
				isToggleButton
			/>
			<QuillButton
				className="ql-list"
				value="check"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.checklist")}
				// No active state cuz react quill doesn't treat this as a toggle button for some reason
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.superscriptSubscript")}>
			<QuillButton
				className="ql-script"
				value="sub"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.subscript")}
				isToggleButton
			/>
			<QuillButton
				className="ql-script"
				value="super"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.superscript")}
				isToggleButton
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.indentation")}>
			<QuillButton
				className="ql-indent"
				value="-1"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.decreaseIndent")}
			/>
			<QuillButton
				className="ql-indent"
				value="+1"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.increaseIndent")}
			/>
		</QuillGroup>

		<QuillGroup>
			<QuillButton
				className="ql-direction"
				value="rtl"
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.rtlText")}
				isToggleButton
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.sizeAndHeadingLevel")}>
			<QuillSelect
				format="size"
				formats={formats}
				onFormatChange={onFormatChange}
				options={fontSizes}
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.fontSize")}
				parser={(raw) => (raw === "" || raw === "normal" ? "" : (raw as FormatValueMap["size"]))}
				renderOption={renderSizeOption}
			/>

			<QuillSelect
				format="header"
				formats={formats}
				onFormatChange={onFormatChange}
				options={headingOptions}
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.headingLevel")}
				parser={(raw) => (raw === "" || raw === "normal" ? "" : (Number(raw) as FormatValueMap["header"]))}
				renderOption={renderHeaderOption}
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.textAndBackgroundColor")}>
			<QuillSelect
				format="color"
				formats={formats}
				onFormatChange={onFormatChange}
				options={textColors}
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.textColor")}
				renderOption={renderColorSquare}
				menuProps={colorMenuProps}
				hideIcon
				renderValue={renderColorValue}
			/>

			<QuillSelect
				format="background"
				formats={formats}
				onFormatChange={onFormatChange}
				options={textColors}
				hideIcon
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.backgroundColor")}
				renderOption={renderColorSquare}
				menuProps={colorMenuProps}
				renderValue={renderBackgroundValue}
			/>
		</QuillGroup>

		<QuillGroup ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.group.textFormatting")}>
			<QuillSelect
				format="font"
				formats={formats}
				onFormatChange={onFormatChange}
				options={fontFamilies}
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.font")}
				renderOption={renderFontOption}
			/>

			<QuillSelect
				format="align"
				formats={formats}
				onFormatChange={onFormatChange}
				options={alignOptions}
				ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.select.textAlignment")}
				renderOption={renderAlignOption}
				renderValue={renderAlignValue}
				hideIcon
				optionHasTooltip
			/>
		</QuillGroup>

		<QuillGroup>
			<QuillButton className="ql-clean" ariaLabel={i18n.t("instructionEditorTSX.ariaLabels.button.removeFormatting")} />
		</QuillGroup>
	</div>
);
